import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { Product } from "schema-dts";
import { User } from "../../types/user";
import { getHighestPrice, getLowestPrice } from "../../helpers/prices";
import { roundPrice } from "../../helpers/money";
import { CurrencyCode, ProductPageProductFragment } from "../../generated/storefront";

type Props = {
  id?: string;
  product: ProductPageProductFragment;
  artist?: User | null;
};

const MetaData = ({ id, product, artist }: Props) => {
  const productLink = `https://www.goodmoodprints.com/products/${product.handle}`;
  const image = product.images.nodes[0];
  const variants = useMemo(() => (product ? product.variants.nodes : []), [product]);
  const lowestPrice = getLowestPrice(variants);
  const hightestPrice = getHighestPrice(variants);
  const generatedTags = product.tags.filter((tag) => tag.startsWith("gmst:")).map((tag) => tag.replace("gmst: ", ""));
  const tagsString = generatedTags.join(", ");
  const description =
    product.description +
    (product.description.length ? " | " : "") +
    tagsString +
    " | Premium fine art print, professionally printed on high-quality giclée paper for exceptional color accuracy and longevity. Our museum-grade prints ensure rich, vibrant details and a luxurious matte finish, making them perfect for home or office decor. Printed using archival inks to prevent fading, each piece is crafted to last a lifetime. Choose from our expertly handmade frames, designed to complement your artwork with a sleek, timeless finish. Elevate your space with timeless art, beautifully framed and made to impress.";

  return (
    <Helmet
      script={[
        helmetJsonLdProp<Product>({
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          description,
          url: productLink,
          image: image.src,
          brand: {
            "@type": "Brand",
            name: "GoodMood Prints",
          },
          offers: {
            "@type": "AggregateOffer",
            price: lowestPrice,
            lowPrice: lowestPrice,
            highPrice: hightestPrice,
            priceCurrency: CurrencyCode.Gbp,
            availability: "https://schema.org/InStock",
          },
        }),
      ]}
    >
      {artist && (
        <title>
          {product.title} | {artist.firstName} {artist.lastName} | GoodMood Prints
        </title>
      )}
      <link rel="canonical" href={`https://www.goodmoodprints.com/products/${product.handle}`} />
      <meta name="description" content={description} />
      <meta property="og:image" content={image.src} />
      <meta name="pinterest-rich-pin" content="true" />
      <meta property="og:type" content="product" />
      <meta property="og:title" content={`${product.title} | GoodMood Prints`} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={productLink} />
      <meta property="og:site_name" content="GoodMood Prints" />
      <meta property="og:google_product_category" content="500044" />
      <meta property="product:brand" content={`${artist?.firstName} ${artist?.lastName}`} />
      <meta property="product:availability" content="in stock" />
      <meta property="product:price:amount" content={`${roundPrice(lowestPrice)}`} />
      <meta property="product:price:currency" content={CurrencyCode.Gbp} />
      <meta property="og:availability" content="instock" />
      <meta property="og:price" content={`${roundPrice(lowestPrice)}`} />
      <meta property="og:priceCurrency" content={CurrencyCode.Gbp} />
      <meta property="g:price" content={`${roundPrice(lowestPrice)} ${CurrencyCode.Gbp}`} />
      <meta itemProp="price" content={lowestPrice.toString()} />
      <meta itemProp="lowPrice" content={lowestPrice.toString()} />
      <meta itemProp="highPrice" content={hightestPrice.toString()} />
      <meta itemProp="priceCurrency" content={CurrencyCode.Gbp} />
      <meta property="product:retailer_item_id" content={id} />
      <meta property="product:category" content="500044" />
    </Helmet>
  );
};

export default MetaData;
